import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useAuthFetch } from "./authFetch";

const CORPUS_API = process.env.REACT_APP_CORPUS_API_URL ?? "http://localhost:3002";

export interface CorpusPermissions {
  admin: boolean;
  read: boolean;
  write: boolean;
  delete: boolean;
}

export interface CorpusListing {
  corpusId: string;
  name: string | null;
  description: string | null;
  kind: "private" | "shared";
  visibility: "public" | "private";
  subscribable: boolean;
  isDefault: boolean;
  owned: boolean;
  subscribed: boolean;
  /** The caller's effective permissions (owner ⇒ all true). */
  permissions: CorpusPermissions;
  documentCount: number;
  lastAddedAt: string | null;
}

export interface CorporaState {
  defaultCorpusId: string | null;
  corpora: CorpusListing[];
  /** Corpora the user can read/search: owned or read-permitted. */
  accessible: CorpusListing[];
  /** Corpora the user can add documents to: owned or write-permitted. */
  writable: CorpusListing[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/** Display label for a corpus: its name, falling back to the id ("My corpus" for the default). */
export function corpusLabel(c: CorpusListing): string {
  if (c.name) return c.name;
  if (c.isDefault) return "My corpus";
  return c.corpusId;
}

/** Fetches the caller's corpus listing from corpus-api. */
function useCorporaState(): CorporaState {
  const authFetch = useAuthFetch();
  const [defaultCorpusId, setDefaultCorpusId] = useState<string | null>(null);
  const [corpora, setCorpora] = useState<CorpusListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    authFetch(`${CORPUS_API}/api/corpora`)
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
        }
        return res.json() as Promise<{ defaultCorpusId: string; corpora: CorpusListing[] }>;
      })
      .then((data) => {
        if (cancelled) return;
        setDefaultCorpusId(data.defaultCorpusId);
        setCorpora(data.corpora ?? []);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Failed to load corpora");
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [authFetch, nonce]);

  return {
    defaultCorpusId,
    corpora,
    accessible: corpora.filter((c) => c.owned || c.permissions.read),
    writable: corpora.filter((c) => c.owned || c.permissions.write),
    loading,
    error,
    refresh,
  };
}

/**
 * Shared corpora state. A single fetch is shared by every page in the
 * dashboard, so a subscribe/unsubscribe/create followed by refresh() is
 * immediately reflected everywhere (Files corpus filter, upload targets,
 * search chips, …) without a manual reload.
 */
const CorporaContext = createContext<CorporaState | null>(null);

export function CorporaProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const state = useCorporaState();
  return React.createElement(CorporaContext.Provider, { value: state }, children);
}

export function useCorpora(): CorporaState {
  const ctx = useContext(CorporaContext);
  if (!ctx) throw new Error("useCorpora must be used within a CorporaProvider");
  return ctx;
}
